import React, { useState, useEffect } from 'react';
import { Clock, RefreshCw, LogOut, AlertTriangle } from 'lucide-react';
import { useSession } from '../contexts/SessionContext';
import './SessionStatus.css';

const SessionStatus = () => {
  const { 
    user, 
    extendSession, 
    logout, 
    getTimeUntilExpiry, 
    getTimeSinceLastActivity,
    sessionExpiry 
  } = useSession();
  
  const [timeUntilExpiry, setTimeUntilExpiry] = useState(null);
  const [timeSinceLastActivity, setTimeSinceLastActivity] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isExtending, setIsExtending] = useState(false);

  useEffect(() => {
    const updateTimes = () => {
      setTimeUntilExpiry(getTimeUntilExpiry());
      setTimeSinceLastActivity(getTimeSinceLastActivity());
    };

    updateTimes();
    const interval = setInterval(updateTimes, 1000); // Update every second

    return () => clearInterval(interval);
  }, [getTimeUntilExpiry, getTimeSinceLastActivity]);

  const formatTime = (milliseconds) => {
    if (!milliseconds) return 'N/A';
    
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const handleExtendSession = async () => {
    setIsExtending(true);
    try {
      await extendSession();
      // Show success message
      setTimeout(() => {
        setIsExtending(false);
      }, 1000);
    } catch (error) {
      console.error('Error extending session:', error);
      setIsExtending(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  const getSessionStatus = () => {
    if (!timeUntilExpiry) return 'unknown';
    
    const minutesUntilExpiry = timeUntilExpiry / (1000 * 60);
    
    if (minutesUntilExpiry <= 5) return 'critical';
    if (minutesUntilExpiry <= 15) return 'warning';
    return 'good';
  };

  const sessionStatus = getSessionStatus();

  if (!user) return null;

  return (
    <div className="session-status">
      <div 
        className={`session-indicator ${sessionStatus}`}
        onClick={() => setShowDetails(!showDetails)}
        title="Click to view session details"
      >
        <Clock size={16} />
        <span className="session-time">
          {timeUntilExpiry ? formatTime(timeUntilExpiry) : 'N/A'}
        </span>
        {sessionStatus === 'critical' && <AlertTriangle size={14} />}
      </div>

      {showDetails && (
        <div className="session-details">
          <div className="session-details-header">
            <h4>Session Information</h4>
            <button 
              className="session-close"
              onClick={() => setShowDetails(false)}
            >
              ×
            </button>
          </div>
          
          <div className="session-info">
            <div className="session-info-item">
              <label>User:</label>
              <span>{user?.email || 'N/A'}</span>
            </div>
            <div className="session-info-item">
              <label>Type:</label>
              <span className={`user-type ${user?.type || ''}`}>{user?.type || 'N/A'}</span>
            </div>
            <div className="session-info-item">
              <label>Time until expiry:</label>
              <span className={`time-remaining ${sessionStatus}`}>
                {timeUntilExpiry ? formatTime(timeUntilExpiry) : 'N/A'}
              </span>
            </div>
            <div className="session-info-item">
              <label>Last activity:</label>
              <span>{timeSinceLastActivity ? formatTime(timeSinceLastActivity) : 'N/A'} ago</span>
            </div>
          </div>

          <div className="session-actions">
            <button
              className="btn-extend"
              onClick={handleExtendSession}
              disabled={isExtending}
            >
              <RefreshCw size={14} className={isExtending ? 'spinning' : ''} />
              {isExtending ? 'Extending...' : 'Extend Session'}
            </button>
            
            <button
              className="btn-logout"
              onClick={handleLogout}
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>

          {sessionStatus === 'critical' && (
            <div className="session-warning">
              <AlertTriangle size={16} />
              <span>Your session will expire soon. Please extend it or save your work.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SessionStatus;
